import { type z } from 'zod';
import { string as __eliadmin_zod_0, object as __eliadmin_zod_1, boolean as __eliadmin_zod_2, email as __eliadmin_zod_3 } from 'zod';
import { ensureZodDefaults as __eliadmin_zod_defaults } from '@eliadmin/core/src/build/zod-defaults';
__eliadmin_zod_defaults();

import type { FormValidationMessages } from '@eliadmin/core/src/hooks/use-form-validation-messages';
import { isValidPhone, splitStreet } from '@eliadmin/core/src/utils/nt/address';

import { newPasswordIssues } from '../shared/new-password-schema';

const text = __eliadmin_zod_0().trim();

const registerFields = __eliadmin_zod_1({
  /** „Registrujem sa ako“: Fyzická osoba (false) | Firma / podnikateľ (true). */
  company_buy: __eliadmin_zod_2(),
  email: text,
  password: __eliadmin_zod_0(),
  password_confirmation: __eliadmin_zod_0(),
  /** The person, for a company its contact person („Kontaktná osoba“). */
  name: text,
  surname: text,
  phone: text,
  company: text,
  ico: text,
  dic: text,
  ic_dph: text,
  /** „Ulica a číslo domu“ in one field (`splitStreet` for the customer record); the company's seat. */
  address: text,
  city: text,
  postcode: text,
  /** Country id. */
  country: text,
  /** „Doručovacia adresa je iná …“ / „Tovar doručiť na inú adresu …“ */
  other_delivery: __eliadmin_zod_2(),
  /** Company only: „Názov prevádzky / príjemcu“ and „Kontaktná osoba na mieste“. */
  delivery_name: text,
  delivery_contact: text,
  delivery_address: text,
  delivery_city: text,
  delivery_postcode: text,
  delivery_country: text,
  /** Terms and privacy in one box – sent as `tos` and `gdpr`. */
  consent: __eliadmin_zod_2(),
  newsletter: __eliadmin_zod_2(),
});

export type RegisterFormInput = z.input<typeof registerFields>;
export type RegisterFormValues = z.output<typeof registerFields>;

/**
 * Fields whose rules depend on the persona (the company's own fields, the seat that a person may
 * leave empty) – their errors are cleared when the customer switches; the submit checks again.
 */
export const PERSONA_FIELDS = [
  'company',
  'ico',
  'dic',
  'ic_dph',
  'delivery_name',
  'address',
  'city',
  'postcode',
] as const;

/**
 * „Vytvoriť konto“ (Figma 221:13538 person, 221:13989 company). The login and the new password
 * with the shared rules (`newPasswordIssues`: the admin rule list, the repeat), the name and an
 * E.164-valid phone (core `registerSchema`); a company adds its identification and the seat, which
 * is required, while a person's billing address is optional – but once started it has to be whole
 * (street with its house number, city, postcode), like the checkout and the profile. A delivery
 * address asked for is required; the consent covers the terms and the privacy policy.
 */
export const registerSchema = (
  VM: FormValidationMessages,
  rulesText: string,
  /** „Objednávka - chýba číslo domu“ (admin); the core „can't be empty“ message as a fallback. */
  streetNumberMissing = VM.CANT_BE_EMPTY_VALIDATION_MESSAGE(),
) =>
  registerFields.superRefine((values, ctx) => {
    const issue = (path: keyof typeof values, message: string) =>
      ctx.addIssue({ code: 'custom', path: [path], message });
    const required = (...paths: (keyof typeof values)[]) => {
      for (const path of paths) {
        if (!values[path]) issue(path, VM.CANT_BE_EMPTY_VALIDATION_MESSAGE());
      }
    };
    const street = (path: 'address' | 'delivery_address') => {
      if (!values[path]) issue(path, VM.CANT_BE_EMPTY_VALIDATION_MESSAGE());
      else if (!splitStreet(values[path]).number) issue(path, streetNumberMissing);
    };

    if (!values.email) issue('email', VM.CANT_BE_EMPTY_VALIDATION_MESSAGE());
    else if (!__eliadmin_zod_3().safeParse(values.email).success) {
      issue('email', VM.EMAIL_INVALID_VALIDATION_MESSAGE());
    }
    for (const { path, message } of newPasswordIssues(values, VM, rulesText)) issue(path, message);

    required('name', 'surname');
    if (!values.phone) issue('phone', VM.CANT_BE_EMPTY_PHONE_VALIDATION_MESSAGE());
    else if (!isValidPhone(values.phone)) issue('phone', VM.PHONE_INVALID_VALIDATION_MESSAGE());

    if (values.company_buy) required('company', 'ico', 'dic');
    if (values.company_buy || values.address || values.city || values.postcode) {
      street('address');
      required('city', 'postcode', 'country');
    }
    if (values.other_delivery) {
      if (values.company_buy) required('delivery_name');
      street('delivery_address');
      required('delivery_city', 'delivery_postcode', 'delivery_country');
    }

    if (!values.consent) {
      issue('consent', `${VM.TOS_VALIDATION_MESSAGE()}. ${VM.GDPR_VALIDATION_MESSAGE()}`);
    }
  });

/** Fields of a 422 from `register` → fields of the form (`gdpr` and `tos` are the one consent). */
export const REGISTER_SERVER_FIELDS: Record<string, keyof RegisterFormValues> = {
  name: 'name',
  surname: 'surname',
  email: 'email',
  phone: 'phone',
  password: 'password',
  password_confirmation: 'password_confirmation',
  gdpr: 'consent',
  tos: 'consent',
};
